import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper'; // some small cards still use Paper for colored stat blocks
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
// cleaned unused list imports
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
// pagination removed: fetch all groups in a single page
// CircularProgress/Divider removed as unused
import ModernLoader from '../components/ModernLoader';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Pagination from '@mui/material/Pagination';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchData } from '../services/api';
import { postData } from '../services/api';
import { fetchUserDetails, fetchTgmaxxGroupList, postLauncherConfigPush, postCleanAppDataConfigPush, postPushMessageConfigPush, postTgmaxxGroupActionList, postTgmaxxGroupActionPush } from '../services/api';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import type { UnifiedDeviceInfo } from '../types/device';
import SourceLink from '../components/SourceLink';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ReplayIcon from '@mui/icons-material/Replay';
import ArticleIcon from '@mui/icons-material/Article';
import BlockIcon from '@mui/icons-material/Block';
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from '@mui/icons-material/Settings';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import SettingsInputHdmiIcon from '@mui/icons-material/SettingsInputHdmi';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// --- TGMAXX Group Action Dialog ---
interface GroupActionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  action: string | null;
  loading: boolean;
  error: string | null;
  taskName: string;
  setTaskName: (v: string) => void;
}
function GroupActionDialog({ open, onClose, onSubmit, action, loading, error, taskName, setTaskName }: GroupActionDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundColor: '#fff' } }}>
      <DialogTitle>{action} - Task Name</DialogTitle>
      <DialogContent sx={{ backgroundColor: '#fff' }}>
        <TextField
          autoFocus
          margin="dense"
          label="Task Name"
          type="text"
          fullWidth
          value={taskName}
          onChange={e => setTaskName(e.target.value)}
          disabled={loading}
          required
        />
        {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained" disabled={loading || !taskName.trim()}>{loading ? 'Submitting...' : 'Submit'}</Button>
      </DialogActions>
    </Dialog>
  );
}


// Minimal local types for group response
type RawGroup = {
  id?: string;
  name?: string;
  groupName?: string;
  devices?: Array<any>;
  totalDevices?: number;
  onlineDeviceAmount?: number;
  activeDeviceAmount?: number;
  groupType?: string;
  description?: string;
  manufacturer?: string;
  modelId?: number;
  model?: string;
  createUser?: string;
  createTime?: string;
  updateTime?: string;
  cfg?: string;
};

export default function GroupsPage({ onSelectDevice }: { onSelectDevice?: (d: UnifiedDeviceInfo) => void }) {
  const navigate = useNavigate();
  const { data: userDetails } = useQuery({
    queryKey: ['user', 'details'],
    queryFn: fetchUserDetails,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const userTenants = React.useMemo(() => {
    if (!userDetails?.userTenants) return [];
    return userDetails.userTenants.filter((ut: any) =>
      ut?.tenant?.name && (
        ut.tenant.name.toLowerCase() === 'tgstream' ||
        ut.tenant.name.toLowerCase() === 'tgmaxx' ||
        ut.tenant.name.toLowerCase() === 'tgstreamtest' ||
        ut.tenant.name.toLowerCase() === 'tgstreamtl4kbr'
      )
    );
  }, [userDetails]);

  const [selectedTenant, setSelectedTenant] = React.useState<string>('');
  React.useEffect(() => {
    if (userTenants.length > 0) {
      if (!selectedTenant || !userTenants.find((ut: any) => ut.tenant.name === selectedTenant)) {
        setSelectedTenant(userTenants[0].tenant.name);
      }
    }
  }, [userTenants, selectedTenant]);

  const isTgmaxxTenant = (selectedTenant ?? '').toLowerCase() === 'tgmaxx';
  const [groups, setGroups] = React.useState<RawGroup[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
  const [sortColumns, setSortColumns] = React.useState<string>('groupName');
  const [orderType, setOrderType] = React.useState<'asc'|'desc'>('asc');
  const [totalGroups, setTotalGroups] = React.useState<number | null>(null);
  const [groupPage, setGroupPage] = React.useState<number>(1);
  const [groupPageSize, setGroupPageSize] = React.useState<number>(20);
  const [groupHasNext, setGroupHasNext] = React.useState<boolean>(false);
  const [tgmaxxPage, setTgmaxxPage] = React.useState<number>(1);
  const [tgmaxxPageSize, setTgmaxxPageSize] = React.useState<number>(20);
  const [tgmaxxHasNext, setTgmaxxHasNext] = React.useState<boolean>(false);

  React.useEffect(() => {
    let mounted = true;
  (async () => {
      try {
    if (mounted) setLoading(true);
    // fetch all groups in a single request
    console.log('Fetching groups with sort:', sortColumns, orderType);
    if (!selectedTenant) return;

  let items: RawGroup[] = [];
  let total: number | null | undefined = undefined;

    if (isTgmaxxTenant) {
      const offset = (tgmaxxPage - 1) * tgmaxxPageSize;
      const res = await fetchTgmaxxGroupList({ offset, limit: tgmaxxPageSize });
      const tgmaxxItems = Array.isArray(res?.data) ? res.data : [];
      items = tgmaxxItems.map((item) => ({
        id: item.id !== undefined && item.id !== null ? String(item.id) : undefined,
        groupName: item.groupName,
        groupType: item.groupType,
        description: item.description,
        manufacturer: item.manufacturer,
        modelId: item.modelId,
        model: item.model,
        createUser: item.createUser,
        createTime: item.createTime,
        updateTime: item.updateTime,
        cfg: item.cfg,
      }));
      total = null;
      setTgmaxxHasNext(tgmaxxItems.length === tgmaxxPageSize);
    } else {
      const qp = `pageNum=${groupPage}&pageSize=${groupPageSize}&sortColumns=${encodeURIComponent(sortColumns)}&orderType=${orderType}`;
      const res = await fetchData(`api/groups/list?${qp}`);
      // Defensive parsing: payload might be res.data or res
      const payload = res?.data ?? res;
      let apiTotal: number | null = null;

      // Common TGStream-style wrapper: payload.data.list and pagination fields
      if (payload && payload.data && Array.isArray(payload.data.list)) {
        items = payload.data.list;
        // prefer explicit pagination totals when present
        const totalValue = payload.data.total ?? payload.data.totalCount ?? undefined;
        if (typeof totalValue === 'number') apiTotal = totalValue;
        else if (typeof totalValue === 'string' && totalValue.trim() !== '') apiTotal = Number(totalValue);
      } else if (Array.isArray(payload)) {
        items = payload;
      } else if (payload && Array.isArray(payload.list)) {
        items = payload.list;
      } else if (payload && Array.isArray(payload.groups)) {
        items = payload.groups;
      }

      if (typeof apiTotal === 'number' && !isNaN(apiTotal)) {
        total = apiTotal;
        setGroupHasNext(false);
      } else {
        total = null;
        setGroupHasNext(items.length === groupPageSize);
      }
    }

            if (mounted) {
              setGroups(items);
              const computedTotal = (typeof total === 'number' && !isNaN(total)) ? total : null;
              setTotalGroups(computedTotal);
            }
      } catch (err) {
        console.error('Failed to load groups', err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [sortColumns, orderType, selectedTenant, isTgmaxxTenant, tgmaxxPage, tgmaxxPageSize, groupPage, groupPageSize]);

  // When groups arrive, select the first group automatically if nothing is selected.
  // Also ensure selectedIndex is cleared if it goes out of range when groups change.
  React.useEffect(() => {
    if (groups.length > 0 && selectedIndex === null) {
      setSelectedIndex(0);
    } else if (selectedIndex !== null && selectedIndex >= groups.length) {
      setSelectedIndex(null);
    }
  }, [groups, selectedIndex]);

  React.useEffect(() => {
    setGroupPage(1);
  }, [sortColumns, orderType, selectedTenant]);
  
  // Reset device page to 1 when selected group changes
  React.useEffect(() => {
    setDevicePage(1);
  }, [selectedIndex]);

  const selected = selectedIndex !== null ? groups[selectedIndex] : null;
  // Devices for the currently selected group (fetched on demand)
  const [groupDevices, setGroupDevices] = React.useState<UnifiedDeviceInfo[]>([]);
  const [deviceLoading, setDeviceLoading] = React.useState<boolean>(false);
  
  // Pagination state for group devices
  const [devicePage, setDevicePage] = React.useState<number>(1);
  const [devicePageSize, setDevicePageSize] = React.useState<number>(20);
  const [totalDevices, setTotalDevices] = React.useState<number>(0);

  // Fetch devices for selected group when selection changes
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      if (isTgmaxxTenant) {
        if (mounted) {
          setGroupDevices([]);
          setTotalDevices(0);
        }
        return;
      }
      if (!selected || !selected.id) {
        if (mounted) {
          setGroupDevices([]);
          setTotalDevices(0);
        }
        return;
      }
      setDeviceLoading(true);
      try {
        // The backend endpoint expects a POST to /api/groups/devices/list with a body like:
        // { entity: { groupId }, pageNum, pageSize, orderType, sortColumns }
        // Use pagination with default page size of 20
        const payload = {
          entity: { groupId: selected.id },
          pageNum: devicePage,
          pageSize: devicePageSize,
          orderType: "desc",
          sortColumns: "deviceStatus",
        };
        const res = await postData(`api/groups/devices/list`, payload);
        // const p = res?.data ?? res;
        let items: any[] = [];
        let total = 0;
        items = res.data.list;
        total = Number(res.data.total) || 0;
       

        if (mounted) {
          console.log('Raw device data from backend:', items.slice(0, 1)); // Debug: log raw data
          console.log('Total devices from GroupDeviceListData.total:', total); // Debug: log total count
          const mapped: UnifiedDeviceInfo[] = items.map((d: any) => ({
            id: d.id ?? d.sn ?? String(Math.random()),
            sn: d.sn,
            mac: d.ethMac ?? d.mac,
            online: d.deviceStatus === 1,
            activate: d.isActive === 1 || d.activate === true,
            manufacturer: d.manufacturer,
            model: d.model,
            city: d.city,
            product: d.product,
            software: d.buildVersion ?? d.software,
            source: d.source,
          }));
          console.log('Group devices mapped:', mapped.slice(0, 1)); // Debug: log first device
          setGroupDevices(mapped);
          setTotalDevices(total);
        }
      } catch (err) {
        console.error('Failed to load group devices', err);
        if (mounted) {
          setGroupDevices([]);
          setTotalDevices(0);
        }
      } finally {
        if (mounted) setDeviceLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [selected, orderType, sortColumns, devicePage, devicePageSize, isTgmaxxTenant]);

  const total = totalDevices; // Use GroupDeviceListData.total from the backend response
  const online = selected?.onlineDeviceAmount ?? groupDevices.filter((d) => d.online).length;
  const activated = selected?.activeDeviceAmount ?? groupDevices.filter((d) => d.activate).length;
  const [tabIndex, setTabIndex] = React.useState<number>(0);
  const [snack, setSnack] = React.useState<{ open: boolean; message: string; severity: 'success'|'error' }>({ open: false, message: '', severity: 'success' });

  // --- TGMAXX Group Actions State (inside component to satisfy hooks rules) ---
  const [actionDialog, setActionDialog] = React.useState<{ open: boolean; action: string | null }>({ open: false, action: null });
  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionTaskName, setActionTaskName] = React.useState('');
  // TGMaxx group actions dropdown state
  const [tgActionsOpen, setTgActionsOpen] = React.useState(false);
  const [tgActionsLoading, setTgActionsLoading] = React.useState(false);
  const [tgActionsItems, setTgActionsItems] = React.useState<Array<any>>([]);
  const [selectedTgActionId, setSelectedTgActionId] = React.useState<number | string | null>(null);

  const handleOpenAction = (action: string) => {
    setActionDialog({ open: true, action });
    setActionTaskName('');
    setActionError(null);
  };
  const handleCloseAction = () => {
    setActionDialog({ open: false, action: null });
    setActionTaskName('');
    setActionError(null);
  };

  // Open TGMaxx action selector for a given type
  const openTgActionSelector = async (type: number) => {
    if (!selected || !selected.id) {
      setSnack({ open: true, message: 'Please select a group first', severity: 'error' });
      return;
    }
    setTgActionsOpen(true);
    setTgActionsLoading(true);
    setTgActionsItems([]);
    setSelectedTgActionId(null);
    try {
      const req = {
        token: null,
        modelName: undefined,
        type: type,
        status: undefined,
        offset: 0,
        limit: 9999,
      };
      const res = await postTgmaxxGroupActionList(req as any);
      // Normalize rows
      const rows = Array.isArray(res?.data?.rows) ? res.data.rows : (Array.isArray(res?.data) ? res.data : []);
      // Filter by groupId equals selected.id (selected.id may be string)
      const gid = Number(selected.id);
      const filtered = rows.filter((r: any) => Number(r.groupId) === gid);
      setTgActionsItems(filtered);
      if (filtered.length === 0) {
        setSnack({ open: true, message: 'No task found for this group and type', severity: 'error' });
      }
    } catch (err: any) {
      console.error('Failed to fetch TGMaxx actions', err);
      setSnack({ open: true, message: err?.message ?? 'Failed to load tasks', severity: 'error' });
    } finally {
      setTgActionsLoading(false);
    }
  };

  const handleTgActionSubmit = async () => {
    // When user picks an action from dropdown, we will POST to backend endpoint
    // The user requested: On clicking any of these actions send api post to /api/tgmaxx/groupAction/list with input: GroupActionListRequest
    // Here we simply re-use the list call as the requirement; typically a different endpoint would execute the action.
    if (!selected || !selected.id) {
      setSnack({ open: true, message: 'No group selected', severity: 'error' });
      return;
    }
    if (!selectedTgActionId) {
      setSnack({ open: true, message: 'Please select an action', severity: 'error' });
      return;
    }
    setActionLoading(true);
    try {
      // Submit a push request to execute the chosen action for the selected group
      const actionItem = tgActionsItems.find((x: any) => String(x.id) === String(selectedTgActionId));
      const taskName = actionItem?.name ?? String(selectedTgActionId);
      const payload = { token: null, groupName: selected.groupName ?? selected.groupName ?? selected.id, taskName } as any;
      const res = await postTgmaxxGroupActionPush(payload);
      if (res?.code === 0 || res?.code === 200 || !res?.code) {
        setSnack({ open: true, message: 'Action pushed successfully', severity: 'success' });
      } else {
        setSnack({ open: true, message: res?.message ?? 'Action push request failed', severity: 'error' });
      }
      setTgActionsOpen(false);
    } catch (err: any) {
      console.error('Failed to submit TGMaxx action', err);
      setSnack({ open: true, message: err?.response?.data?.message ?? err?.message ?? 'Failed to submit action', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // When switching to TGMAXX tenant, default to Control tab (index 0).
  // Do not force tabIndex on every render so user can switch to Details.
  React.useEffect(() => {
    if (isTgmaxxTenant) setTabIndex(0);
  }, [isTgmaxxTenant]);

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    // debug log to ensure tab index updates
    // eslint-disable-next-line no-console
    console.log('Groups tab change =>', newIndex);
    setTabIndex(newIndex);
  };

  const tgmaxxDetailRows = React.useMemo(() => {
    if (!isTgmaxxTenant || !selected) return [];
    return [
      { label: 'Group ID', value: selected.id ?? '—' },
      { label: 'Group Type', value: selected.groupType ?? '—' },
      { label: 'Description', value: selected.description ?? '—' },
      { label: 'Manufacturer', value: selected.manufacturer ?? '—' },
      { label: 'Model', value: selected.model ?? '—' },
      { label: 'Model ID', value: selected.modelId ?? '—' },
      { label: 'Created By', value: selected.createUser ?? '—' },
      { label: 'Created Time', value: selected.createTime ?? '—' },
      { label: 'Updated Time', value: selected.updateTime ?? '—' },
      { label: 'Config', value: selected.cfg ?? '—' },
    ];
  }, [isTgmaxxTenant, selected]);

  const handleTenantChange = React.useCallback((_: React.SyntheticEvent, newValue: string) => {
    setSelectedTenant(newValue);
    setSelectedIndex(null);
    setGroups([]);
    setTotalGroups(null);
    setGroupPage(1);
    setGroupHasNext(false);
    setTgmaxxPage(1);
    setDevicePage(1);
  }, []);

  // Handle device selection - navigate to device details
  const handleSelectDevice = (device: UnifiedDeviceInfo) => {
    if (onSelectDevice) {
      onSelectDevice(device);
    } else {
      // Fallback to direct navigation if no prop provided
      const sn = device?.sn ?? device?.id ?? '';
      navigate(`/devices/${encodeURIComponent(sn)}`);
    }
  };

  const controlBtnSx = (bg: string, color: string = '#fff') => ({
  height: 60,
    borderRadius: '8px',
    backgroundColor: bg,
    color,
    textTransform: 'none',
    '&:hover': { filter: 'brightness(0.95)' },
    '& .MuiButton-startIcon': { mr: 1.5 },
  });

  // Map labels to TGStream group control command codes
  const labelToCommand: Record<string, number> = {
    'Lock': 0,
    'Unlock': 1,
    'Factory Reset': 6,
    'Reboot': 7,
    'Clean Data': 9,
    'Deactivate': 25,
    'Clean Cache': 27,
    'Stand By': 43,
    'HDMI CEC': 81
  };

  const sendGroupControl = async (command: number) => {
    if (!selected || !selected.id) {
      setSnack({ open: true, message: 'Please select a group first', severity: 'error' });
      return;
    }

    // destructive ops confirmation
    if (command === 6 || command === 25) {
      const ok = window.confirm('This operation is destructive. Are you sure you want to continue?');
      if (!ok) return;
    }

    const payload: Record<string, any> = {
      command: command,
      groupId: selected.id,
    };

    // command-specific params
    if (command === 9) {
      // Clean Data expects packageName (optional)
      const pkg = window.prompt('Package name to clean (leave empty to apply default):', '');
      if (pkg === null) return; // cancelled
      if (pkg && pkg.trim()) payload.packageName = pkg.trim();
    }

    if ([6, 7, 25].includes(command)) {
      // these commands may require credentials per API; prompt user
      const user = window.prompt('Admin username (leave empty to skip):', '');
      if (user === null) return;
      const pass = window.prompt('Admin password (leave empty to skip):', '');
      if (pass === null) return;
      if (user.trim()) payload.userName = user.trim();
      if (pass.trim()) payload.password = pass;
    }

    try {
      setSnack({ open: true, message: 'Sending group control...', severity: 'success' });
      const res = await postData(`api/groups/control`, payload);
      // backend returns result object; show success
      setSnack({ open: true, message: `Command ${command} sent to group`, severity: 'success' });
      return res;
    } catch (err: any) {
      console.error('Group control failed', err);
      const detail = err?.response?.data?.message ?? err?.message ?? String(err);
      setSnack({ open: true, message: `Failed: ${detail}`, severity: 'error' });
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" className="page-title">Groups</Typography>
      </Box>

      {selectedTenant !== '' && userTenants.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={selectedTenant} onChange={handleTenantChange} aria-label="tenant tabs">
            {userTenants.map((ut: any) => (
              <Tab
                key={ut.tenant.name}
                label={ut.tenant.displayName || ut.tenant.name}
                value={ut.tenant.name}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            ))}
          </Tabs>
        </Box>
      )}

  <Box sx={{ display: 'flex', gap: 2 }}>
  {/* Left pane: groups */}
  <Paper sx={{ width: 320, p: 1, borderRadius: '8px', border: '2px solid rgba(0,0,0,0.06)' }} elevation={3}>
          {/* spacer removed so controls sit directly under header */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <Box sx={{ flex: 1 }} />
          </Box>

            {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><ModernLoader size={48} /></Box>
          ) : (
            <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 1.5, px: 2 }}>
                      <TableSortLabel
                        active={sortColumns === 'groupName'}
                        direction={orderType}
                        onClick={() => {
                          if (sortColumns === 'groupName') setOrderType((o) => (o === 'asc' ? 'desc' : 'asc'));
                          else setSortColumns('groupName');
                        }}
                      >
                        <span style={{ fontWeight: 600, fontSize: 14, letterSpacing: '0.2px' }}>Group name</span>
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(groups && groups.length) ? groups.map((g, i) => (
                    <TableRow
                      key={g.id ?? i}
                      hover
                      selected={i === selectedIndex}
                      onClick={() => setSelectedIndex(i)}
                      sx={{ cursor: 'pointer', '& .MuiTableCell-root': { py: 1.5, px: 2 } }}
                    >
                      <TableCell>
                        <span style={{ display: 'block', paddingTop: 4, paddingBottom: 4 }}>{g.name ?? g.groupName ?? `Group ${i + 1}`}</span>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell sx={{ py: 2, px: 2 }}>No groups</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {isTgmaxxTenant && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel id="tgmaxx-groups-per-page-label">Per page</InputLabel>
                    <Select
                      labelId="tgmaxx-groups-per-page-label"
                      value={tgmaxxPageSize}
                      label="Per page"
                      onChange={(e) => {
                        const nextSize = Number(e.target.value) || 20;
                        setTgmaxxPageSize(nextSize);
                        setTgmaxxPage(1);
                      }}
                      MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff' } } }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    Page {tgmaxxPage}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    aria-label="previous page"
                    size="small"
                    disabled={tgmaxxPage <= 1 || loading}
                    onClick={() => setTgmaxxPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="next page"
                    size="small"
                    disabled={!tgmaxxHasNext || loading}
                    onClick={() => setTgmaxxPage((p) => p + 1)}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

            {!isTgmaxxTenant && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel id="groups-per-page-label">Per page</InputLabel>
                    <Select
                      labelId="groups-per-page-label"
                      value={groupPageSize}
                      label="Per page"
                      onChange={(e) => {
                        const nextSize = Number(e.target.value) || 20;
                        setGroupPageSize(nextSize);
                        setGroupPage(1);
                      }}
                      MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff' } } }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                  {totalGroups === null && (
                    <Typography variant="body2" color="text.secondary">Page {groupPage}</Typography>
                  )}
                </Box>
                {totalGroups === null ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      aria-label="previous page"
                      size="small"
                      disabled={groupPage <= 1 || loading}
                      onClick={() => setGroupPage((p) => Math.max(1, p - 1))}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label="next page"
                      size="small"
                      disabled={!groupHasNext || loading}
                      onClick={() => setGroupPage((p) => p + 1)}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Pagination
                    count={Math.max(1, Math.ceil(totalGroups / groupPageSize))}
                    page={groupPage}
                    onChange={(_e, v) => setGroupPage(v)}
                    color="primary"
                  />
                )}
              </Box>
            )}
            
            </>
          )}
  </Paper>

  {/* Right pane: group details */}
  <Paper sx={{ flex: 1, p: 1, borderRadius: '8px', border: '2px solid rgba(0,0,0,0.06)' }} elevation={3}>
          <Box sx={{ p: 1, mb: 2 }}>
            <Typography variant="h6">{selected?.name ?? selected?.groupName ?? 'Select a group'}</Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              {isTgmaxxTenant ? (
                <>
                  <Paper sx={{ p: 3, minWidth: 160, minHeight: 86, backgroundColor: '#b7d6fcff' }}>
                    <Typography variant="subtitle2" color="text.secondary">Group Type</Typography>
                    <Typography variant="h5">{selected?.groupType ?? '—'}</Typography>
                  </Paper>
                  <Paper sx={{ p: 3, minWidth: 160, minHeight: 86, backgroundColor: '#a9ffc2ff' }}>
                    <Typography variant="subtitle2" color="text.secondary">Manufacturer</Typography>
                    <Typography variant="h5">{selected?.manufacturer ?? '—'}</Typography>
                  </Paper>
                  <Paper sx={{ p: 3, minWidth: 160, minHeight: 86, backgroundColor: '#bacfffff' }}>
                    <Typography variant="subtitle2" color="text.secondary">Model</Typography>
                    <Typography variant="h5">{selected?.model ?? '—'}</Typography>
                  </Paper>
                </>
              ) : (
                <>
                  <Paper sx={{ p: 3, minWidth: 160, minHeight: 86, backgroundColor: '#b7d6fcff' }}>
                    <Typography variant="subtitle2" color="text.secondary">Total Devices</Typography>
                    <Typography variant="h5">{deviceLoading && devicePage === 1 ? '...' : total}</Typography>
                  </Paper>
                  <Paper sx={{ p: 3, minWidth: 160, minHeight: 86, backgroundColor: '#a9ffc2ff' }}>
                    <Typography variant="subtitle2" color="text.secondary">Online Devices</Typography>
                    <Typography variant="h5">{deviceLoading && devicePage === 1 ? '...' : online}</Typography>
                  </Paper>
                  <Paper sx={{ p: 3, minWidth: 160, minHeight: 86, backgroundColor: '#bacfffff' }}>
                    <Typography variant="subtitle2" color="text.secondary">Activated Devices</Typography>
                    <Typography variant="h5">{deviceLoading && devicePage === 1 ? '...' : activated}</Typography>
                  </Paper>
                </>
              )}
            </Box>
          </Box>


          {/* TGMAXX Group Actions were moved into the Control tab below */}
          {/* Action Dialog */}
          <GroupActionDialog
            open={actionDialog.open}
            onClose={handleCloseAction}
            onSubmit={async () => {
              if (!selected?.groupName || !actionDialog.action) return;
              setActionLoading(true);
              setActionError(null);
              try {
                if (actionDialog.action === 'Launcher config') {
                  await postLauncherConfigPush({ groupName: selected.groupName, taskName: actionTaskName.trim() });
                  setSnack({ open: true, message: 'Launcher configuration push requested', severity: 'success' });
                } else if (actionDialog.action === 'Clean App Data') {
                  await postCleanAppDataConfigPush({ groupName: selected.groupName, taskName: actionTaskName.trim() });
                  setSnack({ open: true, message: 'Clean app data configuration push requested', severity: 'success' });
                } else if (actionDialog.action === 'Push Message') {
                  await postPushMessageConfigPush({ groupName: selected.groupName, taskName: actionTaskName.trim() });
                  setSnack({ open: true, message: 'Push message configuration requested', severity: 'success' });
                }
                handleCloseAction();
              } catch (err: any) {
                const msg = err?.response?.data?.message ?? err?.message ?? 'Request failed';
                setActionError(msg);
              } finally {
                setActionLoading(false);
              }
            }}
            action={actionDialog.action}
            loading={actionLoading}
            error={actionError}
            taskName={actionTaskName}
            setTaskName={setActionTaskName}
          />

          {/* TGMaxx action selector dialog */}
          <Dialog open={tgActionsOpen} onClose={() => setTgActionsOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: '#fff' } }}>
            <DialogTitle>Choose a configuration</DialogTitle>
            <DialogContent sx={{ backgroundColor: '#fff' }}>
              {tgActionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><ModernLoader size={28} /></Box>
              ) : (
                <FormControl fullWidth>
                  {/* <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Action</Typography> */}
                  <Select
                    value={selectedTgActionId ?? ''}
                    displayEmpty
                    onChange={(e) => setSelectedTgActionId(e.target.value as any)}
                    renderValue={(val) => {
                      if (!val) return <span style={{ color: '#888' }}>Select the Task</span>;
                      const it = tgActionsItems.find((x: any) => String(x.id) === String(val));
                      return it?.name ?? String(val);
                    }}
                    MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                  >
                    {/* <MenuItem value="" disabled><em>Select an action</em></MenuItem> */}
                    {tgActionsItems.map((a: any) => (
                      <MenuItem key={a.id} value={a.id}>{a.name ?? `Action ${a.id}`}</MenuItem>
                    ))}
                    {tgActionsItems.length === 0 && <MenuItem disabled>No Task available</MenuItem>}
                  </Select>
                </FormControl>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTgActionsOpen(false)} disabled={actionLoading}>Cancel</Button>
              <Button onClick={handleTgActionSubmit} variant="contained" disabled={actionLoading || tgActionsLoading}>{actionLoading ? 'Submitting...' : 'Submit'}</Button>
            </DialogActions>
          </Dialog>

          <Paper sx={{ p: 1 }} elevation={1}>
            {/* compute available tabs and clamp value to avoid invalid value errors */}
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              onClick={() => console.log('Tabs clicked container')}
              onPointerDown={() => console.log('Tabs pointer down')}
              sx={{ mb: 2, pointerEvents: 'auto', outline: '1px dashed rgba(0,0,0,0.04)', zIndex: 1300 }}
              variant="fullWidth"
              TabIndicatorProps={{ sx: { height: 3, bgcolor: 'primary.main' } }}
            >
              <Tab value={0} label={isTgmaxxTenant ? 'Control' : 'Devices'} />
              <Tab value={1} label={isTgmaxxTenant ? 'Details' : 'Control'} />
            </Tabs>
            

            {tabIndex === 0 && (
              <Box>
                {isTgmaxxTenant ? (
                  <>
                    {/* Control tab for TGMAXX: show same notice as TGStream then action buttons */}
                    <Box sx={{ bgcolor: '#ffdede', p: 2, borderRadius: '8px', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notice</Typography>
                      <Typography variant="body2">Remote operation will perform batch settings on all devices within the group. Please ensure to proceed under safe conditions to avoid affecting the normal use of the users.</Typography>
                    </Box>
                    {selected?.groupName ? (
                      <Box sx={{ mb: 2, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' } }}>
                        <Box>
                          <Button fullWidth sx={controlBtnSx('#2ec4ff')} startIcon={<BuildIcon />} onClick={() => handleOpenAction('Launcher config')}>Launcher config</Button>
                        </Box>
                        <Box>
                          <Button fullWidth sx={controlBtnSx('#a66bd8')} startIcon={<DeleteSweepIcon />} onClick={() => handleOpenAction('Clean App Data')}>Clean App Data</Button>
                        </Box>
                        <Box>
                          <Button fullWidth sx={controlBtnSx('#7f7fff')} startIcon={<ArticleIcon />} onClick={() => handleOpenAction('Push Message')}>Push Message</Button>
                        </Box>
                        <Box>
                          <Button fullWidth sx={controlBtnSx('#ff9aa2')} startIcon={<RestartAltIcon />} onClick={() => openTgActionSelector(1)}>Reboot</Button>
                        </Box>
                        <Box>
                          <Button fullWidth sx={controlBtnSx('#f28350ff')} startIcon={<DeleteForeverIcon />} onClick={() => openTgActionSelector(2)}>Factory Reset</Button>
                        </Box>
                        <Box>
                          <Button fullWidth sx={controlBtnSx('#1fb65b')} startIcon={<DeleteSweepIcon />} onClick={() => openTgActionSelector(3)}>Clear Single App Data</Button>
                        </Box>
                        <Box>
                          <Button fullWidth sx={controlBtnSx('#e8dc2bff')} startIcon={<ArticleIcon />} onClick={() => openTgActionSelector(4)}>Get Logcat</Button>
                        </Box>
                        <Box>
                          <Button fullWidth sx={controlBtnSx('#6c5ce7')} startIcon={<BuildIcon />} onClick={() => openTgActionSelector(5)}>App Upgrade</Button>
                        </Box>
                        <Box>
                          <Button fullWidth sx={controlBtnSx('#d081d8ff')} startIcon={<DeleteSweepIcon />} onClick={() => openTgActionSelector(6)}>Clear Single App Cache</Button>
                        </Box>
                      </Box>
                    ) : null}
                  </>
                ) : (
                  <>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                          <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee' }}>
                            <th style={{ padding: '8px 6px' }}>SN</th>
                            <th style={{ padding: '8px 6px' }}>MAC</th>
                            <th style={{ padding: '8px 6px' }}>Model</th>
                            <th style={{ padding: '8px 6px' }}>Online</th>
                            <th style={{ padding: '8px 6px' }}>Activated</th>
                          </tr>
                      </thead>
                      <tbody>
                        {groupDevices.map((d) => (
                          <tr 
                            key={d.id} 
                            style={{ 
                              borderBottom: '1px solid #f5f5f5', 
                              cursor: 'pointer' 
                            }}
                            onClick={(e) => {
                              try {
                                const sel = typeof window !== 'undefined' && window.getSelection ? window.getSelection()?.toString().trim() : '';
                                if (sel) return; // Don't navigate if user is selecting text
                              } catch (err) {
                                // ignore selection read errors and proceed with click
                              }
                              handleSelectDevice(d);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '';
                            }}
                          >
                            <td style={{ padding: '8px 6px' }}>{d.sn}</td>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.mac ?? '\u2014'}</td>
                            <td style={{ padding: '8px 6px' }}>
                              <SourceLink source={d.source} onClick={(e) => e.stopPropagation()} />
                            </td>
                            <td style={{ padding: '12px 10px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <span className={`app-status-bubble ${d.online ? 'status-green' : 'status-grey'}`} />
                              </span>
                            </td>
                            <td style={{ padding: '12px 10px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <span className={`app-status-bubble ${d.activate ? 'status-green' : 'status-grey'}`} />
                              </span>
                            </td>
                          </tr>
                        ))}
                        {groupDevices.length === 0 && !deviceLoading && (
                          <tr>
                            <td colSpan={5} style={{ padding: 16 }}>
                              {totalDevices === 0 ? 'No devices in this group' : 'No devices on this page'}
                            </td>
                          </tr>
                        )}
                        {deviceLoading && (
                          <tr>
                            <td colSpan={5} style={{ padding: 16, textAlign: 'center' }}>
                              <ModernLoader size={24} />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    
                    {/* Pagination Controls */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel id="devices-per-page-label">Per page</InputLabel>
                          <Select
                            labelId="devices-per-page-label"
                            value={devicePageSize}
                            label="Per page"
                            onChange={(e) => {
                              const newPageSize = Number(e.target.value) || 20;
                              setDevicePageSize(newPageSize);
                              // Reset to first page when page size changes
                              setDevicePage(1);
                            }}
                            MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff' } } }}
                          >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </FormControl>
                        <Typography variant="body2" color="text.secondary">
                          Showing {groupDevices.length} of {totalDevices} devices
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Pagination 
                          count={Math.max(1, Math.ceil(totalDevices / devicePageSize))} 
                          page={devicePage} 
                          onChange={(_e, v) => setDevicePage(v)} 
                          color="primary" 
                        />
                        {deviceLoading && <ModernLoader size={18} />}
                      </Box>
                    </Box>
                  </>
                )}
                </Box>
            )}

            {!isTgmaxxTenant && tabIndex === 1 && (
              <Box>
                <Box sx={{ bgcolor: '#ffdede', p: 2, borderRadius: '8px', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notice</Typography>
                  <Typography variant="body2">Remote operation will perform batch settings on all devices within the group. Please ensure to proceed under safe conditions to avoid affecting the normal use of the users.</Typography>
                </Box>

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' } }}>
                  <Box>
                    <Button fullWidth sx={controlBtnSx('#f39c12')} startIcon={<LockIcon />} onClick={() => sendGroupControl(labelToCommand['Lock'])}>Lock</Button>
                  </Box>
                  <Box>
                    <Button fullWidth sx={controlBtnSx('#2ec4ff')} startIcon={<LockOpenIcon />} onClick={() => sendGroupControl(labelToCommand['Unlock'])}>Unlock</Button>
                  </Box>
                  <Box>
                    <Button fullWidth sx={controlBtnSx('#ff9aa2')} startIcon={<RestartAltIcon />} onClick={() => sendGroupControl(labelToCommand['Reboot'])}>Reboot</Button>
                  </Box>
                  <Box>
                    <Button fullWidth sx={controlBtnSx('#45b39d')} startIcon={<DeleteForeverIcon />} onClick={() => sendGroupControl(labelToCommand['Factory Reset'])}>Factory Reset</Button>
                  </Box>

                  <Box>
                    <Button fullWidth sx={controlBtnSx('#1fb65b')} startIcon={<PowerSettingsNewIcon />} onClick={() => sendGroupControl(labelToCommand['Stand By'])}>Stand By</Button>
                  </Box>
                  <Box>
                    <Button fullWidth sx={controlBtnSx('#a66bd8')} startIcon={<DeleteSweepIcon />} onClick={() => sendGroupControl(labelToCommand['Clean Data'])}>Clean Data</Button>
                  </Box>
                  <Box>
                    <Button fullWidth sx={controlBtnSx('#7f7fff')} startIcon={<ReplayIcon />} onClick={() => sendGroupControl(labelToCommand['Clean Cache'])}>Clean Cache</Button>
                  </Box>
                  {/* <Box>
                    <Button fullWidth disabled sx={{ ...controlBtnSx('#2b8ef7'), backgroundColor: '#ddd', color: '#666' }} startIcon={<ArticleIcon />}>Log Cache</Button>
                  </Box> */}

                  <Box>
                    <Button fullWidth sx={controlBtnSx('#ff6b6b')} startIcon={<BlockIcon />} onClick={() => sendGroupControl(labelToCommand['Deactivate'])}>Deactivate</Button>
                  </Box>
                  {/* <Box>
                    <Button fullWidth disabled sx={{ ...controlBtnSx('#3498db'), backgroundColor: '#ddd', color: '#666' }} startIcon={<BuildIcon />}>Restart Agent</Button>
                  </Box> */}
                  {/* <Box>
                    <Button fullWidth disabled sx={{ ...controlBtnSx('#20c997'), backgroundColor: '#ddd', color: '#666' }} startIcon={<SettingsIcon />}>Preferences</Button>
                  </Box> */}
                  {/* <Box>
                    <Button fullWidth disabled sx={{ ...controlBtnSx('#ffd966', '#000'), backgroundColor: '#ddd', color: '#666' }} startIcon={<WifiTetheringIcon />}>Hotspot</Button>
                  </Box> */}

                  <Box>
                    <Button fullWidth sx={controlBtnSx('#6c5ce7')} startIcon={<SettingsInputHdmiIcon />} onClick={() => sendGroupControl(labelToCommand['HDMI CEC'])}>HDMI CEC</Button>
                  </Box>
                  {/* <Box>
                    <Button fullWidth disabled sx={{ ...controlBtnSx('#f39c12'), backgroundColor: '#ddd', color: '#666' }} startIcon={<AccessTimeIcon />}>Time Zone</Button>
                  </Box> */}
                </Box>
              </Box>
            )}

            {isTgmaxxTenant && tabIndex === 1 && (
              <Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                  {tgmaxxDetailRows.map((row) => (
                    <Box key={row.label} sx={{ border: '1px solid #eee', borderRadius: 2, p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">{row.label}</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>{row.value || '—'}</Typography>
                    </Box>
                  ))}
                  {tgmaxxDetailRows.length === 0 && (
                    <Typography variant="body2" color="text.secondary">Select a group to view details.</Typography>
                  )}
                </Box>
              </Box>
            )}

            <Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
              <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>{snack.message}</Alert>
            </Snackbar>
          </Paper>
  </Paper>
      </Box>
    </>
  );
}
